<template>
  <Modal
    ref="modalRef"
    position="end"
    width="410px"
    frameless-body
    content-class="agent-chat-drawer"
    @after-show="focusComposer"
    @after-close="store.closePanel()"
  >
    <template #header>
      <div class="panel-title">
        <Logo class="logo" />
        <Badge label="Same thread as your texts" variant="green" size="small" />
      </div>
    </template>

    <template #body>
      <AgentChatThread />
    </template>

    <template #footer>
      <div class="composer">
        <FormField class="composer-field">
          <Input
            ref="inputRef"
            v-model="draft"
            type="text"
            placeholder='Reply or ask — e.g. "yes, book it"'
            :disabled="store.sending"
            @keydown.enter="send"
          />
        </FormField>
        <IconButton
          icon="arrow-up"
          variant="primary"
          shape="round"
          size="medium"
          aria-label="Send"
          :disabled="store.sending || !draft.trim()"
          @click="send"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

import { Badge, FormField, IconButton, Input, Logo, Modal } from '@tallo/design-system';

import { useAgentChatStore } from '../stores/agent-chat.store';
import AgentChatThread from './agent-chat-thread.vue';

const store = useAgentChatStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const inputRef = ref<InstanceType<typeof Input>>();
const draft = ref('');

// The store owns open/close (the navbar button and future entry points toggle it); the Modal's
// own paths out (esc, overlay, x-close) sync back through after-close.
watch(
  () => store.isOpen,
  (isOpen) => (isOpen ? modalRef.value?.show() : modalRef.value?.close()),
);

onMounted(() => {
  if (store.isOpen) {
    modalRef.value?.show();
  }
});

function focusComposer(): void {
  inputRef.value?.inputElement?.focus();
}

async function send(): Promise<void> {
  const content = draft.value;

  if (!content.trim()) {
    return;
  }

  draft.value = '';

  const sent = await store.sendMessage(content);

  if (!sent) {
    draft.value = content;
  }

  focusComposer();
}
</script>

<style scoped lang="scss">
.panel-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;

  .logo {
    height: 1.25rem;
    width: auto;
    display: block;
  }
}

.composer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  .composer-field {
    flex: 1;
  }
}
</style>

<style lang="scss">
@import 'styles/mixins/layout.mixins';

// Instance styling via Modal's contentClass — the teleported tree is outside this component's
// scope, so these must be global, namespaced under the drawer class.
.modal-content.agent-chat-drawer {
  .modal-body {
    display: flex;
    flex-direction: column;
    // The thread is the scroller (auto-scroll and lazy-loading hang off its scroll position),
    // so the body must never become one.
    overflow: hidden;
  }

  @include mobile {
    // A chat sheet keeps a stable height instead of growing with every message; the Modal's own
    // max-height still caps it below the top edge.
    &.end {
      height: 100%;
    }

    // At sub-16px font sizes iOS zooms the page on input focus.
    .modal-footer input {
      font-size: 1rem;
    }
  }
}
</style>
